import React, { useMemo, useState } from 'react';
import { Button, Alert, Table } from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import AddOrganizationDialog from '../../AddOrganizationDialog/AddOrganizationDialog';
import OrganizationStatusIndicator from '../../OrganizationStatusIndicator/OrganizationStatusIndicator';
import { CirraServiceType } from '../../../enums/auth';
import { OrgCredential } from '../../../interfaces/organization';
import { OrgEnvironmentType } from '../../../enums/organization';
import { getCredentialsToDisplay } from '../../../utils/credentialUtils';
import { useUserAccess } from '../../../hooks/useUserAccess';

const ENV_ORDER: OrgEnvironmentType[] = [
  OrgEnvironmentType.PRODUCTION,
  OrgEnvironmentType.CUSTOM,
  OrgEnvironmentType.SANDBOX,
  OrgEnvironmentType.DEVELOPER,
  OrgEnvironmentType.SCRATCH,
];

interface SalesforceSetupStepProps {
  onOrgConnected?: () => void;
  isConnected?: boolean;
  existingOrgs?: OrgCredential[];
  defaultOrgId?: string | null;
  onConnectingChange?: (connecting: boolean) => void;
  onMoveToNext?: () => void;
}

export const SalesforceSetupStep: React.FC<SalesforceSetupStepProps> = ({
  onOrgConnected,
  isConnected = false,
  existingOrgs = [],
  defaultOrgId,
  onConnectingChange,
  onMoveToNext,
}) => {
  const [showAddOrg, setShowAddOrg] = useState(false);
  const { currentAccount } = useUserAccess();

  const sortedOrgs = useMemo(() => {
    const filtered = getCredentialsToDisplay(
      existingOrgs,
      ['mcp_selected_at', 'main_selected_at', 'selected_at'],
      currentAccount?.cirra_user_id
    );
    return [...filtered].sort((a, b) => {
      const aEnv = ENV_ORDER.indexOf(a.environment_type as OrgEnvironmentType);
      const bEnv = ENV_ORDER.indexOf(b.environment_type as OrgEnvironmentType);
      // Primary: environment type order. Secondary: timestamp ordering from getCredentialsToDisplay (stable sort preserves it)
      return (
        (aEnv === -1 ? ENV_ORDER.length : aEnv) -
        (bEnv === -1 ? ENV_ORDER.length : bEnv)
      );
    });
  }, [existingOrgs, currentAccount?.cirra_user_id]);

  const hasExistingOrgs = sortedOrgs.length > 0;

  const handleConnectOrg = () => {
    setShowAddOrg(true);
    onConnectingChange?.(true);
  };

  const handleCancelConnect = () => {
    setShowAddOrg(false);
    onConnectingChange?.(false);
  };

  // Show success state if organization is connected
  if (isConnected) {
    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <CheckCircleOutlined
            style={{
              fontSize: 48,
              color: '#52c41a',
              marginBottom: 16,
            }}
          />
          <h3 style={{ color: '#52c41a', margin: 0 }}>
            Salesforce Organization Connected!
          </h3>
          <p style={{ color: 'rgba(0, 0, 0, 0.65)', marginTop: 8 }}>
            Your Salesforce org has been successfully connected to Cirra AI.
          </p>
        </div>

        <Alert
          message="Connection Successful"
          description="You can now proceed to set up the MCP server integration with Claude."
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div className="walkthrough-highlight">
          <div className="walkthrough-highlight-title">
            <SafetyCertificateOutlined /> What&apos;s Next?
          </div>
          <p>
            Now that your Salesforce org is connected, we&apos;ll help you set
            up the MCP server so Claude can interact with your Salesforce org.
          </p>
        </div>
      </div>
    );
  }

  if (showAddOrg) {
    return (
      <div>
        <Alert
          message="Connect Your Salesforce Organization"
          description="You'll be redirected to Salesforce to authorize Cirra AI. For full functionality you will need admin access rights, but you can log in with a more restricted user."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ position: 'relative' }}>
          <AddOrganizationDialog
            service={CirraServiceType.MCP}
            onClose={() => {
              // onClose is called when user cancels or when organization is successfully selected
              // We don't want to trigger onOrgConnected here as it could be a cancellation
              setShowAddOrg(false);
              onConnectingChange?.(false);
            }}
            resetTrigger={1}
            successRedirectTo={null}
            setCredentials={(credentials: OrgCredential[]) => {
              // setCredentials is called only when organization is successfully connected
              // This is the correct place to trigger onOrgConnected
              setShowAddOrg(false);
              onConnectingChange?.(false);
              onOrgConnected?.();
            }}
          />

          {/* Position Cancel button to the left of Submit button */}
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '100px',
              zIndex: 10,
            }}
          >
            <Button onClick={handleCancelConnect} type="default" size="large">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (hasExistingOrgs) {
    const isDefault = (record: OrgCredential) => record.id === defaultOrgId;
    const boldIf = (record: OrgCredential) =>
      isDefault(record) ? { fontWeight: 600 as const } : undefined;

    const columns = [
      {
        title: 'Status',
        key: 'status',
        width: 60,
        render: (_: unknown, record: OrgCredential) => (
          <OrganizationStatusIndicator cred={record} />
        ),
      },
      {
        title: 'Username',
        dataIndex: 'username',
        key: 'username',
        render: (text: string, record: OrgCredential) => (
          <span style={boldIf(record)}>{text}</span>
        ),
      },
      {
        title: 'Organization',
        key: 'org',
        render: (_: unknown, record: OrgCredential) => {
          let hostname: string;
          try {
            hostname = new URL(record.instance_url).hostname;
          } catch {
            hostname = record.instance_url;
          }
          return <span style={boldIf(record)}>{hostname}</span>;
        },
      },
      {
        title: 'Type',
        dataIndex: 'environment_type',
        key: 'environment_type',
        render: (text: string, record: OrgCredential) => (
          <span style={boldIf(record)}>{text}</span>
        ),
      },
    ];

    return (
      <div>
        <p>
          We see you already have the following Salesforce organizations
          connected to Cirra AI:
        </p>

        <Table
          dataSource={sortedOrgs}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="small"
          style={{ marginBottom: 24 }}
        />

        <p>
          You can move on to the next step, or connect an additional
          organization if you prefer.
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Button
            type="primary"
            size="large"
            onClick={onMoveToNext}
            style={{ width: 300 }}
          >
            Move To Next Step
          </Button>
          <Button
            type="default"
            size="large"
            onClick={handleConnectOrg}
            style={{ width: 300 }}
          >
            Connect Another Organization
          </Button>
        </div>

        <div className="walkthrough-highlight">
          <div className="walkthrough-highlight-title">
            <SafetyCertificateOutlined /> Security & Permissions
          </div>
          <p>
            Cirra AI uses OAuth 2.0 for secure authentication. All tokens are
            encrypted and stored securely in our database. We never see or store
            your Salesforce passwords.
          </p>
          <p>
            For additional details on security and access control in Cirra AI
            see the{' '}
            <a
              href="https://docs.cirra.ai/mcp-server/connecting-salesforce#access-rights-and-security"
              target="_blank"
              rel="noopener noreferrer"
            >
              Access rights and security
            </a>{' '}
            section in our documentation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p>
        Now let&apos;s connect your Salesforce organization. Cirra AI needs
        access to your Salesforce org to help you manage configurations, analyze
        data, and automate administrative tasks.
      </p>

      <Button type="primary" size="large" onClick={handleConnectOrg} block>
        Connect Salesforce Organization
      </Button>
      <div className="walkthrough-highlight">
        <div className="walkthrough-highlight-title">
          <SafetyCertificateOutlined /> Security & Permissions
        </div>
        <p>
          Cirra AI uses OAuth 2.0 for secure authentication. All tokens are
          encrypted and stored securely in our database. We do not ask for or
          store your Salesforce passwords.
        </p>
      </div>
    </div>
  );
};
